import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MediaItem } from '../../../../types';

interface TransitionsSectionProps {
  mediaMaker: MediaItem;
  setMediaItems: React.Dispatch<React.SetStateAction<MediaItem[]>>;
  allMediaItems?: MediaItem[]; // NEW: Need all items to find next item
}

export const TransitionsSection: React.FC<TransitionsSectionProps> = ({
  mediaMaker,
  setMediaItems,
  allMediaItems = [],
}) => {
  const { t } = useTranslation(['mediaTab']);

  const TRANSITIONS = [
    { type: 'fade', icon: '🎬', label: t('mediaTab.transitions.types.fade') },
    { type: 'dissolve', icon: '💫', label: t('mediaTab.transitions.types.dissolve') },
    { type: 'wipeleft', icon: '⬅️', label: t('mediaTab.transitions.types.wipeleft') },
    { type: 'wiperight', icon: '➡️', label: t('mediaTab.transitions.types.wiperight') },
    { type: 'wipeup', icon: '⬆️', label: t('mediaTab.transitions.types.wipeup') },
    { type: 'wipedown', icon: '⬇️', label: t('mediaTab.transitions.types.wipedown') },
    { type: 'slideleft', icon: '◀️', label: t('mediaTab.transitions.types.slideleft') },
    { type: 'slideright', icon: '▶️', label: t('mediaTab.transitions.types.slideright') },
    { type: 'circleopen', icon: '⭕', label: t('mediaTab.transitions.types.circleopen') },
    { type: 'circleclose', icon: '⚫', label: t('mediaTab.transitions.types.circleclose') },
    { type: 'fadeblack', icon: '⬛', label: t('mediaTab.transitions.types.fadeblack') },
    { type: 'pixelize', icon: '🔲', label: t('mediaTab.transitions.types.pixelize') },
    { type: 'radial', icon: '🎯', label: t('mediaTab.transitions.types.radial') },
    { type: 'diagtl', icon: '↖️', label: t('mediaTab.transitions.types.diagtl') },
    { type: 'diagbr', icon: '↘️', label: t('mediaTab.transitions.types.diagbr') },
  ];

  // Find the next media item (sorted by start time, excluding hidden items)
  const nextMediaItem = useMemo(() => {
    const sortedItems = [...allMediaItems]
      .filter(item => !item.hidden) // Exclude hidden items
      .sort((a, b) => a.start - b.start);
    
    const currentIndex = sortedItems.findIndex(item => item.id === mediaMaker.id);
    if (currentIndex === -1 || currentIndex === sortedItems.length - 1) {
      return null; // No next item (current is last or not found)
    }
    
    return sortedItems[currentIndex + 1];
  }, [allMediaItems, mediaMaker.id]);

  // Check if transition is valid (items are connected enough)
  const transitionValidation = useMemo(() => {
    if (!nextMediaItem) {
      return {
        isValid: false,
        reason: t('mediaTab.transitions.noNextClip'),
        gap: null,
        minRequired: null,
        actualOverlap: 0
      };
    }

    const gap = nextMediaItem.start - mediaMaker.end;
    
    // Calculate actual overlap between clips
    // If gap is negative, clips overlap (overlap = abs(gap))
    // If gap is positive, no overlap (overlap = 0)
    const actualOverlap = gap <= 0 ? Math.abs(gap) : 0;
    
    // Transition is valid if clips overlap or touch
    const isValid = gap <= 0;
    
    return {
      isValid,
      reason: isValid 
        ? null 
        : t('mediaTab.transitions.clipsApart', { gap: gap.toFixed(2) }),
      gap,
      minRequired: 0, // Must overlap or touch
      actualOverlap
    };
  }, [mediaMaker, nextMediaItem, t]);

  const handleTransitionToggle = (transitionType: string) => {
    // Check if transition is valid before toggling
    if (!transitionValidation.isValid && mediaMaker.transitionType !== transitionType) {
      return; // Don't allow selecting transition if not valid
    }

    setMediaItems((prevMediaItems) =>
      prevMediaItems.map((mediaItem) =>
        mediaItem.id === mediaMaker.id
          ? {
              ...mediaItem,
              transitionType: mediaItem.transitionType === transitionType ? undefined : transitionType,
              // Set transitionDuration to the actual overlap amount
              transitionDuration:
                mediaItem.transitionType === transitionType
                  ? undefined
                  : transitionValidation.actualOverlap,
            }
          : mediaItem
      )
    );
  };

  const handleClearTransition = () => {
    setMediaItems((prevMediaItems) =>
      prevMediaItems.map((mediaItem) =>
        mediaItem.id === mediaMaker.id
          ? {
              ...mediaItem,
              transitionType: undefined,
              transitionDuration: undefined,
            }
          : mediaItem
      )
    );
  };

  // Auto-clear transition when items move apart
  React.useEffect(() => {
    if (mediaMaker.transitionType && !transitionValidation.isValid) {
      handleClearTransition();
    }
  }, [transitionValidation.isValid, mediaMaker.transitionType]);

  return (
    <div
      style={{
        marginTop: '1rem',
        padding: '1rem',
        background: '#2d2d2d',
        borderRadius: '8px',
        border: mediaMaker.transitionType && transitionValidation.isValid 
          ? '2px solid #3b82f6' // Blue border when transition is active
          : '1px solid #3a3a3a',
      }}
    >
      <label
        style={{
          marginBottom: '0.75rem',
          display: 'block',
          color: '#e0e0e0',
          fontSize: '0.9rem',
          fontWeight: '600',
        }}
      >
        {t('mediaTab.transitions.title')}
      </label>

      {/* Validation Warning */}
      {!transitionValidation.isValid && (
        <div
          style={{
            marginBottom: '0.75rem',
            padding: '0.75rem',
            background: '#3a2a2a',
            borderRadius: '6px',
            border: '1px solid #663333',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
            <span style={{ fontSize: '1rem' }}>⚠️</span>
            <span style={{ color: '#ff6b6b', fontSize: '0.85rem', fontWeight: '600' }}>
              {t('mediaTab.transitions.unavailable')}
            </span>
          </div>
          <div style={{ color: '#cc9999', fontSize: '0.75rem', lineHeight: '1.4' }}>
            {transitionValidation.reason}
          </div>
          {transitionValidation.gap !== null && transitionValidation.gap > 0 && (
            <div style={{ color: '#999', fontSize: '0.7rem', marginTop: '0.5rem', fontStyle: 'italic' }}>
              {t('mediaTab.transitions.tipOverlap')}
            </div>
          )}
        </div>
      )}

      {/* Scrollable Transition Grid */}
      <div
        style={{
          maxHeight: '280px',
          overflowY: 'auto',
          overflowX: 'hidden',
          paddingRight: '0.25rem',
          marginBottom: '0.75rem',
          opacity: transitionValidation.isValid ? 1 : 0.4,
          pointerEvents: transitionValidation.isValid ? 'auto' : 'none',
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '0.5rem',
          }}
        >
          {TRANSITIONS.map(({ type, icon, label }) => (
            <button
              key={type}
              onClick={() => handleTransitionToggle(type)}
              disabled={!transitionValidation.isValid}
              style={{
                padding: '0.5rem',
                background: mediaMaker.transitionType === type ? '#4a4a4a' : '#3a3a3a',
                color: '#e0e0e0',
                border:
                  mediaMaker.transitionType === type ? '2px solid #3b82f6' : '1px solid #555',
                borderRadius: '6px',
                cursor: transitionValidation.isValid ? 'pointer' : 'not-allowed',
                fontSize: '0.75rem',
                fontWeight: mediaMaker.transitionType === type ? '600' : '400',
                transition: 'all 0.2s ease',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '0.25rem',
              }}
            >
              <span style={{ fontSize: '1.1rem' }}>{icon}</span>
              <span>{label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Overlap Display (Read-Only) - Replaces Duration Slider */}
      {mediaMaker.transitionType && transitionValidation.isValid && (
        <div style={{ marginTop: '0.75rem' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '0.5rem',
            }}
          >
            <span style={{ color: '#e0e0e0', fontSize: '0.85rem' }}>
              {t('mediaTab.transitions.overlap')}
            </span>
            <span
              style={{
                color: '#3b82f6',
                fontSize: '0.85rem',
                fontFamily: 'monospace',
                fontWeight: '600',
              }}
            >
              {transitionValidation.actualOverlap.toFixed(1)}s
            </span>
          </div>
        </div>
      )}

      {/* Clear Transition Button */}
      {mediaMaker.transitionType && transitionValidation.isValid && (
        <button
          onClick={handleClearTransition}
          style={{
            width: '100%',
            marginTop: '0.75rem',
            padding: '0.5rem',
            background: '#3a3a3a',
            color: '#e0e0e0',
            border: '1px solid #555',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.85rem',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#4a4a4a';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = '#3a3a3a';
          }}
        >
          {t('mediaTab.transitions.clearTransition')}
        </button>
      )}

      {/* Info Text */}
      <div
        style={{
          marginTop: '0.75rem',
          fontSize: '0.75rem',
          color: mediaMaker.transitionType && transitionValidation.isValid ? '#3b82f6' : '#888',
          fontStyle: 'italic',
          lineHeight: '1.4',
        }}
      >
        {mediaMaker.transitionType && transitionValidation.isValid
          ? t('mediaTab.transitions.activeTransition', { 
              type: mediaMaker.transitionType, 
              duration: transitionValidation.actualOverlap.toFixed(1)
            })
          : !nextMediaItem
          ? t('mediaTab.transitions.lastClip')
          : t('mediaTab.transitions.mustOverlap')}
      </div>
    </div>
  );
};
